export interface Point {
  x: number;
  y: number;
}

export interface LineAttributes {
  id: string;
  points: number[];
  anchor?: Array<{ id: string; x: number; y: number }>;
}

export interface AnchorResult {
  success: boolean;
  updatedPoints?: number[];
  anchor?: { id: string; x: number; y: number };
  error?: string;
}

export function addAnchorToLine(
  event: any,
  xAnchor: number,
  yAnchor: number,
  id: string,
  items: any[] = []
): AnchorResult {
  try {
    const line = event.target.attrs as LineAttributes

    if (!line.points || line.points.length < 4) {
      return {
        success: false,
        error: 'Line does not have enough points to add anchor'
      }
    }

    // Find the closest point on the line (projection)
    const { projectedPoint, segmentIndex } = findProjectedPointOnLine(
      line.points,
      xAnchor,
      yAnchor
    )

    if (segmentIndex === -1) {
      return {
        success: false,
        error: 'Could not find suitable segment for anchor placement'
      }
    }

    // Insert the projected point after the segment start point
    const insertIndex = (segmentIndex * 2) + 2

    // Create updated points array
    const updatedPoints = [...line.points]
    updatedPoints.splice(insertIndex, 0, projectedPoint.x, projectedPoint.y)

    // Create new anchor
    const newAnchor = {
      id,
      x: projectedPoint.x,
      y: projectedPoint.y
    }

    // Update Vue reactive data if items array is provided
    if (items.length > 0) {
      const item = items.find(item => item.id === line.id)
      if (item) {
        item.points = [...updatedPoints]
        if (!item.anchor) item.anchor = []
        item.anchor.push(newAnchor)
      }
    }

    return {
      success: true,
      updatedPoints,
      anchor: newAnchor
    }

  } catch (error) {
    console.error('Error adding anchor to line:', error)
    return {
      success: false,
      error: `Failed to add anchor: ${error instanceof Error ? error.message : 'Unknown error'}`
    }
  }
}

/**
 * Finds the projected point on the closest line segment
 */
function findProjectedPointOnLine(
  points: number[],
  targetX: number,
  targetY: number
): { projectedPoint: Point; segmentIndex: number } {
  if (points.length < 4) {
    return { projectedPoint: { x: targetX, y: targetY }, segmentIndex: -1 }
  }

  let minDistance = Infinity
  let closestSegmentIndex = -1
  let closestProjectedPoint: Point = { x: targetX, y: targetY }

  for (let i = 0; i < points.length - 2; i += 2) {
    const segmentStart: Point = {
      x: points[i],
      y: points[i + 1]
    }
    const segmentEnd: Point = {
      x: points[i + 2],
      y: points[i + 3]
    }

    const { point: projectedPoint, distance } = projectPointToSegment(
      targetX,
      targetY,
      segmentStart.x,
      segmentStart.y,
      segmentEnd.x,
      segmentEnd.y
    )

    if (distance < minDistance) {
      minDistance = distance
      closestSegmentIndex = i / 2
      closestProjectedPoint = projectedPoint
    }
  }

  return {
    projectedPoint: closestProjectedPoint,
    segmentIndex: closestSegmentIndex
  }
}

/**
 * Projects a point onto a line segment and returns the projected point and distance
 */
function projectPointToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): { point: Point; distance: number } {
  const A = px - x1
  const B = py - y1
  const C = x2 - x1
  const D = y2 - y1

  const dot = A * C + B * D
  const lenSq = C * C + D * D

  let param = 0
  if (lenSq !== 0) {
    param = Math.max(0, Math.min(1, dot / lenSq))
  }

  const xx = x1 + param * C
  const yy = y1 + param * D

  const dx = px - xx
  const dy = py - yy
  const distance = Math.sqrt(dx * dx + dy * dy)

  return {
    point: { x: xx, y: yy },
    distance
  }
}
